import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { FiAlertCircle, FiArrowRight, FiLock, FiMail, FiPhone, FiUser } from 'react-icons/fi';

const Register = ({ setAuth }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    if (registerError) {
      setRegisterError('');
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = 'Nama harus diisi';
    }

    if (!formData.email) {
      newErrors.email = 'Email harus diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }

    if (!formData.phone) {
      newErrors.phone = 'Nomor telepon harus diisi';
    } else if (!/^[0-9]{10,13}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Nomor telepon tidak valid';
    }

    if (!formData.password) {
      newErrors.password = 'Password harus diisi';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Konfirmasi password harus diisi';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Password tidak cocok';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    setRegisterError('');

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: 'customer'
      };

      const response = await API.post('/api/auth/register', userData);

      localStorage.setItem('token', response.data.token);

      setAuth({
        isAuthenticated: true,
        user: response.data.user,
        loading: false
      });

        navigate('/customer/dashboard');
      } catch (error) {
        console.error('Registration error:', error);
        setRegisterError(
          error.response?.data?.message || 'Terjadi kesalahan saat mendaftar. Silakan coba lagi.'
        );
      } finally {
        setIsLoading(false);
      }
    };

  const inputBase = 'w-full rounded-full border py-3 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2';

  return (
    <div className="min-h-screen bg-white px-4 py-16 sm:px-6">
      <div className="mx-auto w-full max-w-5xl">
        <div className="grid gap-12 border-t border-slate-200 pt-20 lg:grid-cols-[0.8fr_1fr]">
          <div className="max-w-md">
            <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Register</div>
            <h1 className="mt-5 text-4xl font-bold text-slate-950">Buat akun baru.</h1>
            <p className="mt-5 text-base leading-8 text-slate-600">
              Form registrasi dibuat lebih ringan dan lebih fokus ke isi. Tanpa panel tebal dan tanpa shadow berlebihan.
            </p>
          </div>

          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-secondary">Register</h2>
                <p className="mt-1 text-sm text-gray-500">Buat akun baru</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-300 text-xl font-bold text-slate-900">
                <span className="text-primary">X</span>S
              </div>
            </div>

            {registerError && (
              <div className="mb-4 flex items-start rounded border border-red-200 bg-red-50 p-3 text-sm">
                <FiAlertCircle className="mr-2 mt-0.5 flex-shrink-0 text-red-500" />
                <span className="text-red-700">{registerError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 border-t border-slate-200 pt-6">
              <div>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <FiUser className="text-gray-400" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className={`${inputBase} ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                    placeholder="Nama Lengkap"
                  />
                </div>
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
              </div>

              <div>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <FiMail className="text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`${inputBase} ${errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                    placeholder="Email"
                  />
                </div>
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>

              <div>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <FiPhone className="text-gray-400" />
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`${inputBase} ${errors.phone ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                    placeholder="Nomor Telepon"
                  />
                </div>
                {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <FiLock className="text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`${inputBase} ${errors.password ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                      placeholder="Password"
                    />
                  </div>
                  {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
                </div>

                <div>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <FiLock className="text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`${inputBase} ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                      placeholder="Konfirmasi Password"
                    />
                  </div>
                  {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-4 flex w-full items-center justify-center rounded-full bg-primary px-4 py-3 font-medium text-white transition hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-t-2 border-white" />
                    <span>Memproses...</span>
                  </div>
                ) : (
                  <>
                    <span>Daftar</span>
                    <FiArrowRight className="ml-2" />
                  </>
                )}
              </button>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Sudah punya akun?{' '}
                  <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
                    Login
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
